import * as React from 'react';
import compose from 'recompose/compose';
import { Route, Switch, Redirect, withRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import Loadable from 'react-loadable';
import { ErrorBoundary } from 'src/components/error';
import { CLIENT_URL } from './api/constants';
import generateMetaInfo from 'shared/generate-meta-info';
import GlobalStyles from './reset.css.js';
import { GlobalThreadAttachmentStyles } from 'src/components/message/threadAttachment/style';
import { theme } from 'shared/theme';
import AppViewWrapper from 'src/components/appViewWrapper';
import Head from 'src/components/head';
import ModalRoot from 'src/components/modals/modalRoot';
import Gallery from 'src/components/gallery';
import Composer from 'src/components/composer';
import signedOutFallback from 'src/helpers/signed-out-fallback';
import TopNavigation from 'src/components/navigation';
import PrivateChannelJoin from 'src/views/privateChannelJoin';
import PrivateCommunityJoin from 'src/views/privateCommunityJoin';
import ThreadSlider from 'src/views/threadSlider';
import Navigation from 'src/views/navigation';
import DirectMessages from 'src/views/directMessages';
import { UserProfile } from './views/UserProfile';
import { withCurrentUser } from 'src/components/withCurrentUser';

import RedirectOldThreadRoute from './views/thread/redirect-old-route';
import NewUserOnboarding from './views/newUserOnboarding';
import QueryParamToastDispatcher from './views/queryParamToastDispatcher';
import { LoadingView } from 'src/views/viewHelpers';
import GlobalTitlebar from 'src/views/globalTitlebar';
import NoUsernameHandler from 'src/views/authViewHandler/noUsernameHandler';
import { NavigationContext } from 'src/helpers/navigation-context';
import TermsAndConditionsModal from 'src/components/modals/TermsAndConditionsModal';
import dataProvider from 'shared/rest';
import { canCreateGroup } from 'src/helpers/adminPanelControl';
import AppcuesLoader from './AppcuesLoader';
import { AddMetadata } from './utils/Reporter';

const HomePage = Loadable({
  loader: () => import('./views/HomePage' /* webpackChunkName: "Explore" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

/* prettier-ignore */
const UserView = Loadable({
  loader: () => import('./views/user'/* webpackChunkName: "UserView" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

/* prettier-ignore */
const CommunityView = Loadable({
  loader: () => import('./views/community'/* webpackChunkName: "CommunityView" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

/* prettier-ignore */
const ChannelView = Loadable({
  loader: () => import('./views/channel'/* webpackChunkName: "ChannelView" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

/* prettier-ignore */
const HomeViewRedirect = Loadable({
  loader: () => import('./views/homeViewRedirect'/* webpackChunkName: "HomeViewRedirect" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

/* prettier-ignore */
const Notifications = Loadable({
  loader: () => import('./views/notifications'/* webpackChunkName: "Notifications" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

/* prettier-ignore */
const UserSettings = Loadable({
  loader: () => import('./views/userSettings'/* webpackChunkName: "UserSettings" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

/* prettier-ignore */
const CommunitySettings = Loadable({
  loader: () => import('./views/communitySettings'/* webpackChunkName: "communitySettings" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

/* prettier-ignore */
const ChannelSettings = Loadable({
  loader: () => import('./views/channelSettings'/* webpackChunkName: "channelSettings" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

/* prettier-ignore */
const NewCommunity = Loadable({
  loader: () => import('./views/admin/pages/newCommunity'/* webpackChunkName: "NewCommunity" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

const Admin = Loadable({
  loader: () => import('./views/admin' /* webpackChunkName: "" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

const GroupDetail = Loadable({
  loader: () =>
    import('./views/admin/pages/groups/detail' /* webpackChunkName: "" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

const PostDetail = Loadable({
  loader: () => import('./views/PostDetail' /* webpackChunkName: "" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

const EventDetail = Loadable({
  loader: () => import('./views/EventDetail' /* webpackChunkName: "" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

const Experts = Loadable({
  loader: () =>
    import('./views/admin/pages/experts' /* webpackChunkName: "" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

const Members = Loadable({
  loader: () =>
    import('./views/admin/pages/members' /* webpackChunkName: "" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

const Posts = Loadable({
  loader: () => import('./views/admin/pages/posts' /* webpackChunkName: "" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

const GroupIdeas = Loadable({
  loader: () =>
    import('./views/admin/pages/groupIdeas' /* webpackChunkName: "" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

const Events = Loadable({
  loader: () => import('./views/admin/pages/events' /* webpackChunkName: "" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

const Comments = Loadable({
  loader: () =>
    import('./views/admin/pages/comments' /* webpackChunkName: "" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

/* prettier-ignore */
const NewDirectMessage = Loadable({
  loader: () => import('./views/newDirectMessage'/* webpackChunkName: "NewDirectMessage" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

/* prettier-ignore */
const Search = Loadable({
  loader: () => import('./views/search'/* webpackChunkName: "Search" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />,
});

// <Route path="/profile/:userId" component={UserProfile} />

/* prettier-ignore */
const ErrorFallback = Loadable({
  loader: () => import('./components/error'/* webpackChunkName: "Error" */),
  loading: ({ isLoading }) => isLoading && <LoadingView />
});

const HomeViewRedirectFallback = signedOutFallback(HomeViewRedirect);
const HomeFallback = signedOutFallback(HomeViewRedirect, () => (
  <Redirect to="/" />
));
// const LoginFallback = signedOutFallback(() => <Redirect to="/" />, Login);
// const CommunityLoginFallback = signedOutFallback(
//   props => <Redirect to={`/${props.match.params.communitySlug}`} />,
//   CommunityLoginView
// );
const UserProfileFallback = signedOutFallback(UserProfile, () => <></>);
const NewCommunityFallback = signedOutFallback(NewCommunity, () => <></>);
const AdminFallback = signedOutFallback(Admin, () => <></>);

const GroupFallback = signedOutFallback(GroupDetail, () => <></>);

const ExpertsFallback = signedOutFallback(Experts, () => <></>);

const MembersFallback = signedOutFallback(Members, () => <></>);

const PostsFallback = signedOutFallback(Posts, () => <></>);

const EventsFallback = signedOutFallback(Events, () => <></>);

const GroupIdeasFallback = signedOutFallback(GroupIdeas, () => <></>);

const CommentsFallback = signedOutFallback(Comments, () => <></>);

const NewDirectMessageFallback = signedOutFallback(NewDirectMessage, () => (
  <></>
));
const MessagesFallback = signedOutFallback(DirectMessages, () => <></>);
const UserSettingsFallback = signedOutFallback(UserSettings, () => <></>);
const CommunitySettingsFallback = signedOutFallback(CommunitySettings, () => (
  <></>
));
const ChannelSettingsFallback = signedOutFallback(ChannelSettings, () => <></>);
const NotificationsFallback = signedOutFallback(Notifications, () => <></>);
const ComposerFallback = signedOutFallback(Composer, () => <></>);

export const RouteModalContext = React.createContext({
  isModal: false,
});

class Routes extends React.Component {
  previousLocation = this.props.location;
  state = { navigationIsOpen: false, isTermsAndConditionsModalOpen: false };

  setNavigationIsOpen = val => this.setState({ navigationIsOpen: val });

  agreeToTermsAndConditions = () => {
    const { currentUser } = this.props;
    const { id } = currentUser;

    const params = {
      id,
      data: {
        hasAgreedToTerms: true,
      },
    };
    dataProvider.update('users', params).then(res => {
      if (window.Appcues) {
        window.Appcues.track('first_time_accessing_community');
      }
      window.location.reload();
    });
  };

  componentDidUpdate(prevProps) {
    // NOTE: in order to have access to this information, you will need
    // to wrap this component in the `withRouter` HOC

    const {
      location: { pathname },
    } = this.props;
    const previousLocation = prevProps.location.pathname;
    if (pathname !== previousLocation && window.Appcues) {
      window.Appcues.page();
    }
  }

  render() {
    const { currentUser } = this.props;

    const { navigationIsOpen } = this.state;
    const { title, description } = generateMetaInfo();

    if (this.props.maintenanceMode) {
      return <ThemeProvider theme={theme}></ThemeProvider>;
    }
    const { location } = this.props;
    if (currentUser) {
      // Add Tracking Metadata.
      const { id, role } = currentUser;
      const contextMetadata = {
        isAnonymous: 'false',
        userId: id,
        userRoles: role,
      };
      AddMetadata(contextMetadata);

      // check terms and condition
      const { hasAgreedToTerms } = currentUser;
      const { isTermsAndConditionsModalOpen } = this.state;
      if (!hasAgreedToTerms) {
        if (!isTermsAndConditionsModalOpen) {
          this.setState({
            isTermsAndConditionsModalOpen: true,
          });
        }
        return (
          <TermsAndConditionsModal
            title="Terms and Conditions"
            open={isTermsAndConditionsModalOpen}
            submit={this.agreeToTermsAndConditions}
          />
        );
      }
      // check view admin
      const pathname = location.pathname;
      const canViewAdmin =
        currentUser && currentUser.role && currentUser.canViewAdmin;
      if (
        pathname.includes('/admin') ||
        pathname.includes('/manage/editGroup') ||
        pathname.includes('/manage/experts') ||
        pathname.includes('/manage/members') ||
        pathname.includes('/manage/posts') ||
        pathname.includes('/manage/events') ||
        pathname.includes('/manage/groupideas') ||
        pathname.includes('/manage/comments')
      ) {
        if (!canViewAdmin) {
          return (
            <div>
              You have no permission to access to the Crimson Community
              Management Panel. Please talk to the Community Admin to get an
              access.
            </div>
          );
        }
      }
      if (pathname.includes('/manage/new/community')) {
        if (!canCreateGroup(currentUser.role)) {
          return (
            <div>You can't create a group, please ask the Community Admin.</div>
          );
        }
      }
    } else {
      //log in
      return <div></div>;
    }

    const isModal = false; /* !!(
      location.state &&
      location.state.modal &&
      this.previousLocation !== location
    ); // not initial render */

    // allows any UI in the tree to open or close the side navigation on mobile
    const navigationContext = {
      navigationIsOpen,
      setNavigationIsOpen: this.setNavigationIsOpen,
    };

    // allows any UI in the tree to know if it is existing within a modal or not
    // commonly used for background views to know that they are backgrounded
    const routeModalContext = { isModal };
    return (
      <ErrorBoundary fallbackComponent={ErrorFallback}>
        <ThemeProvider theme={theme}>
          <NavigationContext.Provider value={navigationContext}>
            {/* default meta tags, get overridden by anything further down the tree */}
            <Head title={title} description={description} />
            <GlobalStyles />
            <GlobalThreadAttachmentStyles />
            <ErrorBoundary>
              <Gallery />
            </ErrorBoundary>
            <ErrorBoundary>
              <ModalRoot />
            </ErrorBoundary>
            <ErrorBoundary>
              <QueryParamToastDispatcher />
            </ErrorBoundary>
            <ErrorBoundary>
              <TopNavigation location={location} currentUser={currentUser} />
            </ErrorBoundary>

            {/* 
              while users should be able to browse communities/threads
              if they are signed out (eg signedOutFallback), they should not
              be allowed to use the app after signing up if they dont set a username.

              otherwise we can get into a state where people are sending DMs,
              sending messages, and posting threads without having a user profile
              that people can report or link to.

              this global component simply listens for users without a username
              to be authenticated, and if so forces a redirect to /new/user 
              prompting them to set a username
            */}
            <ErrorBoundary>
              <NoUsernameHandler currentUser={currentUser} />
            </ErrorBoundary>

            {isModal && (
              <Route
                // NOTE(@mxstbr): This custom path regexp matches threadId correctly in all cases, no matter if we prepend it with a custom slug or not.
                // Imagine our threadId is "id-123-id" (similar in shape to an actual UUID)
                // - /id-123-id => id-123-id, easy start that works
                // - /some-custom-slug~id-123-id => id-123-id, custom slug also works
                // - /~id-123-id => id-123-id => id-123-id, empty custom slug also works
                // - /some~custom~slug~id-123-id => id-123-id, custom slug with delimiter char in it (~) also works! :tada:
                path="/:communitySlug/:channelSlug/(.*~)?:threadId"
                component={props => (
                  <ThreadSlider
                    previousLocation={this.previousLocation}
                    {...props}
                  />
                )}
              />
            )}

            {/*
              this context provider allows children views to determine
              how they should behave if a modal is open. For example,
              you could tell a community view to not paginate the thread
              feed if a thread modal is open.
            */}
            <RouteModalContext.Provider value={routeModalContext}>
              {/*
                we tell the app view wrapper any time the modal state
                changes so that we can restore the scroll position to where
                it was before the modal was opened
              */}
              <AppViewWrapper {...routeModalContext}>
                <Route component={Navigation} />
                <Route component={GlobalTitlebar} />
                <div css={isModal ? { overflow: 'hidden' } : {}}>
                  {/*
                    switch only renders the first match. Subrouting happens downstream
                    https://reacttraining.com/react-router/web/api/Switch
                  */}
                  <Switch location={isModal ? this.previousLocation : location}>
                    <Route
                      exact
                      path="/"
                      component={HomeViewRedirectFallback}
                    />
                    <Route exact path="/home" component={HomeFallback} />

                    {/* Admin Pages */}
                    <Route path="/admin" component={AdminFallback} />
                    <Route
                      path="/manage/editGroup/:groupId"
                      component={GroupFallback}
                    />
                    <Route
                      path="/manage/experts/:groupId"
                      component={ExpertsFallback}
                    />
                    <Route
                      path="/manage/members/:groupId"
                      component={MembersFallback}
                    />
                    <Route
                      path="/manage/posts/:groupId"
                      component={PostsFallback}
                    />
                    <Route
                      path="/manage/posts/:groupId"
                      component={PostsFallback}
                    />
                    <Route
                      path="/manage/events/:groupId"
                      component={EventsFallback}
                    />
                    <Route
                      path="/manage/groupideas"
                      component={GroupIdeasFallback}
                    />

                    <Route
                      path="/manage/comments/:threadId"
                      component={CommentsFallback}
                    />

                    {/* App Pages */}
                    <Route
                      path="/manage/new/community"
                      component={NewCommunityFallback}
                    />
                    <Route
                      path="/profile/:userId"
                      component={UserProfileFallback}
                    />
                    <Route path="/new/thread" component={ComposerFallback} />
                    <Route path="/new/search" component={Search} />
                    <Route path="/new/user" component={NewUserOnboarding} />
                    <Route
                      path="/new/message"
                      component={NewDirectMessageFallback}
                    />

                    <Route
                      path="/new"
                      render={() => <Redirect to="/new/community" />}
                    />

                    <Route path="/explore" component={HomePage} />
                    <Route
                      path="/messages/:threadId"
                      component={MessagesFallback}
                    />
                    <Route path="/messages" component={MessagesFallback} />
                    <Route
                      path="/thread/:threadId"
                      component={RedirectOldThreadRoute}
                    />
                    <Route path="/thread" render={() => <Redirect to="/" />} />
                    <Route
                      exact
                      path="/users"
                      render={() => <Redirect to="/" />}
                    />
                    <Route exact path="/users/:username" component={UserView} />
                    <Route
                      exact
                      path="/users/:username/settings"
                      component={UserSettingsFallback}
                    />
                    <Route
                      path="/notifications"
                      component={NotificationsFallback}
                    />

                    {/*
                        We check communitySlug last to ensure none of the above routes
                        pass. We handle null communitySlug values downstream by either
                        redirecting to home or showing a 404
                      */}
                    <Route
                      path="/:communitySlug/:channelSlug/settings"
                      component={ChannelSettingsFallback}
                    />
                    <Route
                      path="/:communitySlug/:channelSlug/join/:token"
                      component={PrivateChannelJoin}
                    />
                    <Route
                      path="/:communitySlug/:channelSlug/join"
                      component={PrivateChannelJoin}
                    />
                    <Route
                      path="/:communitySlug/settings"
                      component={CommunitySettingsFallback}
                    />
                    <Route
                      path="/:communitySlug/join/:token"
                      component={PrivateCommunityJoin}
                    />
                    {/* <Route
                      path="/:communitySlug/login"
                      component={CommunityLoginFallback}
                    /> */}
                    <Route
                      path="/:communitySlug/post/:threadId"
                      component={PostDetail}
                    />
                    <Route
                      path="/:communitySlug/event/:eventId"
                      currentUser={currentUser}
                      component={EventDetail}
                    />
                    <Route
                      path="/:communitySlug/:channelSlug"
                      component={ChannelView}
                    />
                    <Route path="/:communitySlug" component={CommunityView} />
                  </Switch>
                </div>

                {isModal && (
                  <Route
                    path="/thread/:threadId"
                    component={RedirectOldThreadRoute}
                  />
                )}

                {isModal && (
                  <Route
                    path="/new/thread"
                    render={props => (
                      <ComposerFallback
                        {...props}
                        previousLocation={this.previousLocation}
                        isModal
                      />
                    )}
                  />
                )}

                {isModal && (
                  <Route
                    path="/new/message"
                    render={props => (
                      <NewDirectMessageFallback
                        {...props}
                        previousLocation={this.previousLocation}
                        isModal
                      />
                    )}
                  />
                )}
              </AppViewWrapper>
            </RouteModalContext.Provider>
          </NavigationContext.Provider>
          <AppcuesLoader currentUser={currentUser} />
        </ThemeProvider>
      </ErrorBoundary>
    );
  }
}

export default compose(withRouter, withCurrentUser)(Routes);
